import { useMutation } from "@apollo/client";
import mixpanel from "mixpanel-browser";
import { useNavigate } from "react-router-dom";

import DELETE_TEAM from "../../../graphql/mutations/DeleteTeam";
import { useAuth, useTeam } from "../../../hooks";
import { alert } from "../../common/Alert";
import { ConfirmModal } from "../../common/modals/PopupModals";

export function DeleteTeamModal({ shouldShow, hideModal, team }) {
  const navigate = useNavigate();
  const { userUuid, userTeams, setUserTeams } = useAuth();
  const { setCurrentTeam, emptyTeam, currentTeam } = useTeam();
  const [deleteTeam, { loading }] = useMutation(DELETE_TEAM);

  const isOnlyMember =
    team?.users?.length === 1 && team?.users[0]?.uuid === userUuid;

  const handleDeleteTeam = async () => {
    if (!isOnlyMember) return;

    try {
      const { data } = await deleteTeam({
        variables: {
          teamUuid: team.uuid,
        },
      });

      if (data?.deleteTeam?.ok) {
        alert("success", "Team deleted successfully.");
        mixpanel.track("Team Deleted", {
          teamUuid: team.uuid,
          teamName: team.name,
        });
        if (currentTeam?.uuid === team.uuid) {
          setCurrentTeam(emptyTeam);
        }
        setUserTeams(userTeams.filter((t) => t.uuid !== team.uuid));
      } else {
        alert(
          "error",
          data?.deleteTeam?.message || "Failed to delete team.",
        );
      }
      navigate("/teams");
    } catch (error) {
      alert("error", "Failed to delete team. Please try again.");
      console.error(error);
      mixpanel.track("Delete Team Error", {
        teamUuid: team.uuid,
        error: error.message,
      });
    }
  };

  return (
    <ConfirmModal
      shouldShow={shouldShow}
      hideModal={hideModal}
      title="Delete Team"
      confirmText="Delete"
      cancelText="Cancel"
      confirmAction={handleDeleteTeam}
      cancelAction={hideModal}
      disabled={loading || !isOnlyMember}
    >
      <div>
        {isOnlyMember ? (
          <>
            <h5>
              Are you sure you want to delete{" "}
              <strong>{team?.name}</strong>? This action cannot be
              undone.
            </h5>
            <div className="bg-red-100 dark:bg-red-600/50 border-red-500 border p-2 rounded-md my-4">
              This will permanently delete the team and all associated
              data.
            </div>
          </>
        ) : (
          <>
            <h5>
              You cannot delete <strong>{team?.name}</strong> while
              there are other team members.
            </h5>
            <div className="bg-yellow-100 dark:bg-yellow-600/50 border-yellow-500 border p-2 rounded-md my-4">
              To delete this team, you must first remove all other
              team members. Once you are the only member, you can
              delete the team.
            </div>
          </>
        )}
      </div>
    </ConfirmModal>
  );
}
